import React from 'react';
import { ThumbsUp, ThumbsDown, MessageCircle, TrendingUp } from 'lucide-react';

export interface EngagementData {
  comment_id: number;
  upvotes: number;
  downvotes: number;
  score: number;
  reply_count: number;
  positive_replies: number;
  negative_replies: number;
  neutral_replies: number;
  engagement_rate: number;
  last_checked: string;
}

interface EngagementMetricsProps {
  data: EngagementData | null;
  loading?: boolean;
}

export function EngagementMetrics({ data, loading }: EngagementMetricsProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="grid grid-cols-4 gap-4">
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
            <div className="h-12 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-500 text-center">
          No engagement data yet. Data will appear after the comment has been monitored.
        </p>
      </div>
    );
  }

  const sentimentPercentages = {
    positive: data.reply_count > 0 ? (data.positive_replies / data.reply_count) * 100 : 0,
    negative: data.reply_count > 0 ? (data.negative_replies / data.reply_count) * 100 : 0,
    neutral: data.reply_count > 0 ? (data.neutral_replies / data.reply_count) * 100 : 0,
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Engagement Metrics</h3>
        <span className="text-xs text-gray-500">
          Updated: {new Date(data.last_checked).toLocaleString()}
        </span>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Upvotes */}
        <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
          <div className="flex-shrink-0">
            <ThumbsUp className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-600">Upvotes</p>
            <p className="text-xl font-bold text-gray-900">{data.upvotes}</p>
          </div>
        </div>

        {/* Downvotes */}
        <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
          <div className="flex-shrink-0">
            <ThumbsDown className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-xs text-gray-600">Downvotes</p>
            <p className="text-xl font-bold text-gray-900">{data.downvotes}</p>
          </div>
        </div>

        {/* Score */}
        <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
          <div className="flex-shrink-0">
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xs text-gray-600">Score</p>
            <p className="text-xl font-bold text-gray-900">{data.score}</p>
          </div>
        </div>

        {/* Replies */}
        <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
          <div className="flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-xs text-gray-600">Replies</p>
            <p className="text-xl font-bold text-gray-900">{data.reply_count}</p>
          </div>
        </div>
      </div>

      {/* Engagement Rate */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">Engagement Rate</span>
          <span className="text-lg font-bold text-gray-900">
            {(data.engagement_rate * 100).toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(data.engagement_rate * 100, 100)}%` }}
          />
        </div>
      </div>

      {/* Reply Sentiment Breakdown */}
      {data.reply_count > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Reply Sentiment</h4>
          <div className="space-y-2">
            {/* Positive */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Positive</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {data.positive_replies}
                </span>
                <span className="text-xs text-gray-500">
                  ({sentimentPercentages.positive.toFixed(0)}%)
                </span>
              </div>
            </div>

            {/* Neutral */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                <span className="text-sm text-gray-600">Neutral</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {data.neutral_replies}
                </span>
                <span className="text-xs text-gray-500">
                  ({sentimentPercentages.neutral.toFixed(0)}%)
                </span>
              </div>
            </div>

            {/* Negative */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-sm text-gray-600">Negative</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  {data.negative_replies}
                </span>
                <span className="text-xs text-gray-500">
                  ({sentimentPercentages.negative.toFixed(0)}%)
                </span>
              </div>
            </div>
          </div>

          {/* Visual Sentiment Bar */}
          <div className="mt-3 w-full h-2 bg-gray-200 rounded-full overflow-hidden flex">
            {data.positive_replies > 0 && (
              <div
                className="bg-green-500 h-full"
                style={{ width: `${sentimentPercentages.positive}%` }}
              />
            )}
            {data.neutral_replies > 0 && (
              <div
                className="bg-gray-400 h-full"
                style={{ width: `${sentimentPercentages.neutral}%` }}
              />
            )}
            {data.negative_replies > 0 && (
              <div
                className="bg-red-500 h-full"
                style={{ width: `${sentimentPercentages.negative}%` }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
